import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { MOCK_PLAYLIST } from '../mock/rooms';
import { fetchCastCommand, fetchRoomDanmaku, fetchRoomDisplay, fetchRoomWxacode } from '../services/partyScreenApi';
import ServiceCallPanel from '../components/ServiceCallPanel';
import {
  exitFullscreen,
  isFullscreenActive,
  requestElementFullscreen,
  shouldAutoFullscreen,
} from '../utils/fullscreen';
import { normalizeScreenStyle } from '../constants/screenStyles';
import { isRoomEntryGranted } from '../constants/roomAccess';
import RoomPasswordGate from '../components/RoomPasswordGate';
import './RoomDisplay.css';
import './RoomDisplay.themes.css';

function CastAccessDock({ accessKey, wxacode, wxacodeLoading, wxacodeError, variant = 'panel' }) {
  if (!accessKey) return null;
  return (
    <aside className={`cast-access-dock cast-access-dock--${variant}`} aria-label="投屏密令与太阳码">
      <div className="cast-access-key-block">
        <span className="cast-access-key-label">投屏密令</span>
        <span className="cast-access-key-value" title={accessKey}>{accessKey}</span>
      </div>
      <div className="cast-access-wxacode-block">
        {wxacodeLoading ? (
          <span className="cast-access-wxacode-loading">太阳码加载中…</span>
        ) : wxacode ? (
          <>
            <img className="cast-access-wxacode-img" src={wxacode} alt="勒里聚刻小程序太阳码" />
            <p className="cast-access-wxacode-hint">微信扫码进入小程序</p>
          </>
        ) : wxacodeError ? (
          <p className="cast-access-wxacode-error">{wxacodeError}</p>
        ) : (
          <span className="cast-access-wxacode-loading">太阳码暂不可用</span>
        )}
      </div>
    </aside>
  );
}

function CastFullscreenOverlay({
  slides,
  slideIndex,
  onExit,
  onSelectSlide,
  accessKey,
  wxacode,
  wxacodeLoading,
  wxacodeError,
}) {
  useEffect(() => {
    document.documentElement.classList.add('cast-fs-active');
    document.body.classList.add('cast-fs-active');
    document.body.style.overflow = 'hidden';
    const onKey = (e) => {
      if (e.key === 'Escape') onExit();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.documentElement.classList.remove('cast-fs-active');
      document.body.classList.remove('cast-fs-active');
      document.body.style.overflow = '';
      window.removeEventListener('keydown', onKey);
    };
  }, [onExit]);

  const hasSlides = slides.length > 0;

  return createPortal(
    <div className="cast-fs-overlay" role="dialog" aria-modal="true" aria-label="投屏全屏">
      <div className="cast-fs-overlay-main">
        {hasSlides ? (
          <>
            <div className="cast-fs-overlay-stack">
              {slides.map((slide, idx) => (
                <img
                  key={slide.id}
                  src={slide.image_url}
                  alt={slide.title || '投屏图片'}
                  className={idx === slideIndex ? 'is-visible' : ''}
                />
              ))}
            </div>
            {slides[slideIndex]?.title ? (
              <p className="cast-fs-overlay-caption">{slides[slideIndex].title}</p>
            ) : null}
            {slides.length > 1 ? (
              <div className="cast-fs-overlay-dots">
                {slides.map((s, idx) => (
                  <button
                    key={s.id}
                    type="button"
                    className={idx === slideIndex ? 'is-active' : ''}
                    aria-label={`第 ${idx + 1} 张`}
                    onClick={() => onSelectSlide(idx)}
                  />
                ))}
              </div>
            ) : null}
          </>
        ) : (
          <div className="cast-fs-overlay-empty">
            <p className="cast-fs-overlay-empty-title">投屏内容已清空</p>
            <p className="cast-fs-overlay-empty-hint">客人可在小程序继续上传图片，或按 Esc / 点击右上角退出全屏</p>
          </div>
        )}
      </div>
      <CastAccessDock
        accessKey={accessKey}
        wxacode={wxacode}
        wxacodeLoading={wxacodeLoading}
        wxacodeError={wxacodeError}
        variant="overlay"
      />
      <button type="button" className="cast-fs-overlay-exit" onClick={onExit}>
        退出全屏
      </button>
    </div>,
    document.body
  );
}

function CastPanel({ roomId, onFullscreenChange }) {
  const screenRef = useRef(null);
  const loadRef = useRef(null);
  const revisionRef = useRef('');
  const [display, setDisplay] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [slideIndex, setSlideIndex] = useState(0);
  const [fullscreen, setFullscreen] = useState(false);
  const [overlayFullscreen, setOverlayFullscreen] = useState(false);
  const [wxacode, setWxacode] = useState('');
  const [wxacodeLoading, setWxacodeLoading] = useState(false);
  const [wxacodeError, setWxacodeError] = useState('');

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const data = await fetchRoomDisplay(roomId);
        if (!cancelled) {
          if (data?.cast_revision) revisionRef.current = data.cast_revision;
          setDisplay(data);
          setError('');
          setSlideIndex((i) => {
            const len = data?.slides?.length || 0;
            if (!len) return 0;
            return Math.min(i, len - 1);
          });
        }
      } catch (e) {
        if (!cancelled) setError(e?.message || '加载投屏失败');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    loadRef.current = load;
    load();
    const poll = setInterval(load, 8000);
    return () => {
      cancelled = true;
      loadRef.current = null;
      clearInterval(poll);
    };
  }, [roomId]);

  const slides = display?.slides || [];
  const intervalSec = Number(display?.slide_interval_sec) || 8;
  const slideKey = slides.map((s) => s.id).join(',');
  const hasSlides = slides.length > 0;
  const castActive = !!(display?.enabled && hasSlides);
  const accessKey = display?.access_key || '';
  const shouldLoadWxacode = !!accessKey;
  const isFullscreenUi = fullscreen || overlayFullscreen;
  const canFullscreen = !loading && !error && (hasSlides || !!accessKey || !!wxacode);
  const showFullscreenBtn = canFullscreen || isFullscreenUi;

  useEffect(() => {
    if (!roomId || !shouldLoadWxacode) {
      setWxacode('');
      setWxacodeError('');
      return undefined;
    }
    let cancelled = false;
    const loadCode = async () => {
      setWxacodeLoading(true);
      try {
        const data = await fetchRoomWxacode(roomId);
        if (!cancelled) {
          setWxacode(data?.image_base64 || '');
          setWxacodeError('');
        }
      } catch (e) {
        if (!cancelled) {
          setWxacode('');
          setWxacodeError(e?.message || '太阳码加载失败');
        }
      } finally {
        if (!cancelled) setWxacodeLoading(false);
      }
    };
    loadCode();
    return () => {
      cancelled = true;
    };
  }, [roomId, shouldLoadWxacode, accessKey]);

  const exitOverlay = () => {
    setOverlayFullscreen(false);
    onFullscreenChange?.(false);
    exitFullscreen().catch(() => {});
  };

  useEffect(() => {
    if (!overlayFullscreen || loading) return undefined;
    if (!hasSlides) {
      exitOverlay();
    }
    return undefined;
  }, [overlayFullscreen, hasSlides, loading]);

  useEffect(() => {
    if (!castActive || slides.length <= 1) return undefined;
    const timer = setInterval(() => {
      setSlideIndex((i) => (i + 1) % slides.length);
    }, Math.max(3, intervalSec) * 1000);
    return () => clearInterval(timer);
  }, [castActive, slideKey, slides.length, intervalSec]);

  useEffect(() => {
    if (!roomId) return undefined;
    let cancelled = false;
    const pollCommand = async () => {
      try {
        const cmd = await fetchCastCommand(roomId);
        if (cancelled) return;
        if (cmd?.cast_revision && cmd.cast_revision !== revisionRef.current) {
          revisionRef.current = cmd.cast_revision;
          loadRef.current?.();
        }
        if (!cmd?.action) return;
        if (cmd.action === 'enter_fullscreen') {
          setOverlayFullscreen(true);
          onFullscreenChange?.(true);
        } else if (cmd.action === 'exit_fullscreen') {
          setOverlayFullscreen(false);
          onFullscreenChange?.(false);
          exitFullscreen().catch(() => {});
        }
      } catch {
        /* 轮询失败忽略 */
      }
    };
    pollCommand();
    const timer = setInterval(pollCommand, 500);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [roomId, onFullscreenChange]);

  useEffect(() => {
    const onChange = () => setFullscreen(isFullscreenActive());
    document.addEventListener('fullscreenchange', onChange);
    document.addEventListener('webkitfullscreenchange', onChange);
    return () => {
      document.removeEventListener('fullscreenchange', onChange);
      document.removeEventListener('webkitfullscreenchange', onChange);
    };
  }, []);

  useEffect(() => {
    if (!shouldAutoFullscreen() || !castActive || !screenRef.current) return;
    requestElementFullscreen(screenRef.current).catch(() => {});
  }, [castActive, slideKey]);

  const toggleFullscreen = async () => {
    if (overlayFullscreen || fullscreen) {
      exitOverlay();
      return;
    }
    if (!screenRef.current) return;
    try {
      if (hasSlides) {
        setOverlayFullscreen(true);
        onFullscreenChange?.(true);
        return;
      }
      if (isFullscreenActive()) {
        await exitFullscreen();
        onFullscreenChange?.(false);
      } else {
        await requestElementFullscreen(screenRef.current);
        onFullscreenChange?.(true);
      }
    } catch {
      if (hasSlides) {
        setOverlayFullscreen(true);
        onFullscreenChange?.(true);
      }
    }
  };

  const accessDockProps = {
    accessKey,
    wxacode,
    wxacodeLoading,
    wxacodeError,
  };

  return (
    <>
      {overlayFullscreen ? (
        <CastFullscreenOverlay
          slides={slides}
          slideIndex={slideIndex}
          onExit={exitOverlay}
          onSelectSlide={setSlideIndex}
          {...accessDockProps}
        />
      ) : null}
      <section className="cast-panel">
      <div className="panel-head cast-panel-head">
        <div>
          <h2 className="panel-title">投屏</h2>
          <p className="panel-subtitle cast-panel-subtitle">
            {castActive
              ? `共 ${slides.length} 张 · 每 ${intervalSec} 秒切换`
              : '扫码或输入密令投图'}
          </p>
        </div>
        {showFullscreenBtn ? (
          <button type="button" className="cast-fullscreen-btn" onClick={toggleFullscreen}>
            {isFullscreenUi ? '退出全屏' : '全屏展示'}
          </button>
        ) : null}
      </div>
      <div
        ref={screenRef}
        className={`cast-screen${castActive ? ' cast-screen--active' : ''}${accessKey ? ' cast-screen--with-dock' : ''}`}
      >
        {loading ? (
          <span className="cast-screen-label">加载中…</span>
        ) : error ? (
          <>
            <span className="cast-screen-label">加载失败</span>
            <p>{error}</p>
          </>
        ) : castActive ? (
          <div className="cast-screen-layout">
            <div className="cast-screen-main">
              <div className="cast-slide-stack">
                {slides.map((slide, idx) => (
                  <img
                    key={slide.id}
                    src={slide.image_url}
                    alt={slide.title || '投屏图片'}
                    className={`cast-slide-image${idx === slideIndex ? ' is-visible' : ''}`}
                  />
                ))}
              </div>
              {slides[slideIndex]?.title ? (
                <p className="cast-slide-caption">{slides[slideIndex].title}</p>
              ) : null}
              {slides.length > 1 ? (
                <div className="cast-slide-dots">
                  {slides.map((s, idx) => (
                    <button
                      key={s.id}
                      type="button"
                      className={`cast-slide-dot${idx === slideIndex ? ' is-active' : ''}`}
                      aria-label={`第 ${idx + 1} 张`}
                      onClick={() => setSlideIndex(idx)}
                    />
                  ))}
                </div>
              ) : null}
            </div>
            <CastAccessDock {...accessDockProps} variant="panel" />
          </div>
        ) : slides.length > 0 && !display?.enabled ? (
          <>
            <span className="cast-screen-label">投屏已关闭</span>
            <p>管理后台已暂停该房间投屏，开启后将自动展示 {slides.length} 张图片</p>
            <CastAccessDock {...accessDockProps} variant="panel" />
          </>
        ) : (
          <div className="cast-screen-layout cast-screen-layout--empty">
            <div className="cast-empty-wxacode">
              <span className="cast-screen-label">暂无投屏内容</span>
              <p className="cast-empty-wxacode-tip">请客人扫码或输入密令，在小程序上传图片</p>
            </div>
            <CastAccessDock {...accessDockProps} variant="panel" />
          </div>
        )}
      </div>
    </section>
    </>
  );
}

function DanmakuOverlay({ roomId }) {
  const [items, setItems] = useState([]);
  const [enabled, setEnabled] = useState(true);

  useEffect(() => {
    if (!roomId) return undefined;
    let cancelled = false;
    const load = async () => {
      try {
        const data = await fetchRoomDanmaku(roomId);
        if (!cancelled) {
          setEnabled(data?.enabled !== false);
          setItems(Array.isArray(data?.list) ? data.list : []);
        }
      } catch {
        if (!cancelled) setItems([]);
      }
    };
    load();
    const timer = setInterval(load, 5000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [roomId]);

  if (!enabled || !items.length) return null;

  return (
    <div className="danmaku-overlay" aria-hidden="true">
      {items.map((item, index) => (
        <div
          key={item.id}
          className="danmaku-item"
          style={{
            '--dm-color': item.color || '#d48806',
            '--dm-top': `${6 + (index % 4) * 26}px`,
            '--dm-delay': `${(index % 8) * 1.2}s`,
            '--dm-duration': `${14 + (index % 3) * 2}s`,
          }}
        >
          <strong>{item.sender_name || '访客'}：</strong>
          {item.content}
        </div>
      ))}
    </div>
  );
}

function MusicPanel() {
  const playing = MOCK_PLAYLIST.find((t) => t.playing);
  return (
    <section className="music-panel">
      <div className="panel-head">
        <h2 className="panel-title">背景音乐</h2>
      </div>
      <div className="now-playing">
        <div className="now-playing-disc" aria-hidden="true" />
        <div>
          <div className="now-playing-label">正在播放</div>
          <div className="now-playing-title">{playing?.title || '—'}</div>
          <div className="now-playing-artist">{playing?.artist || ''}</div>
        </div>
      </div>
      <ul className="playlist">
        {MOCK_PLAYLIST.map((track) => (
          <li key={track.id} className={track.playing ? 'is-playing' : ''}>
            <span className={`playlist-indicator${track.playing ? ' is-playing' : ''}`} aria-hidden="true" />
            <span className="playlist-title">{track.title}</span>
            <span className="playlist-artist">{track.artist}</span>
          </li>
        ))}
      </ul>
      <div className="feature-actions feature-actions--disabled">
        <button type="button" disabled>上一首</button>
        <button type="button" disabled>播放 / 暂停</button>
        <button type="button" disabled>下一首</button>
      </div>
      <p className="feature-hint">功能开发中，当前为界面预览</p>
    </section>
  );
}

export default function RoomDisplay() {
  const { roomId } = useParams();
  const navigate = useNavigate();
  const [roomInfo, setRoomInfo] = useState(null);
  const [storeLogo, setStoreLogo] = useState('');
  const [screenStyle, setScreenStyle] = useState('warm');
  const [loading, setLoading] = useState(true);
  const [castFullscreen, setCastFullscreen] = useState(false);
  const [entryGranted, setEntryGranted] = useState(() => isRoomEntryGranted());

  useEffect(() => {
    if (!entryGranted) return undefined;
    let cancelled = false;
    const load = async () => {
      try {
        const data = await fetchRoomDisplay(roomId);
        if (!cancelled) {
          setRoomInfo(data?.room || null);
          setStoreLogo(data?.room?.store_logo || '');
          setScreenStyle(normalizeScreenStyle(data?.screen_style));
        }
      } catch {
        if (!cancelled) {
          setRoomInfo(null);
          setStoreLogo('');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    const poll = setInterval(load, 3000);
    return () => {
      cancelled = true;
      clearInterval(poll);
    };
  }, [roomId, entryGranted]);

  if (!entryGranted) {
    return (
      <div className="room-display-page room-display-page--empty">
        <RoomPasswordGate
          open
          onCancel={() => navigate('/')}
          onSuccess={() => setEntryGranted(true)}
        />
      </div>
    );
  }

  if (!loading && !roomInfo) {
    return (
      <div className="room-display-page room-display-page--empty">
        <p>未找到该房间</p>
        <Link to="/">返回房间列表</Link>
      </div>
    );
  }

  return (
    <div className={`room-display-page theme-${screenStyle}${castFullscreen ? ' room-display-page--cast-fs' : ''}`}>
      <div className="room-display-bg" />
      <DanmakuOverlay roomId={roomId} />

      <div className="display-top">
        <header className="display-header">
          <div className="display-header-brand">
            <button type="button" className="back-btn" onClick={() => navigate('/')}>
              返回
            </button>
            <div className="display-logo-slot" aria-label="品牌标识">
              {storeLogo ? (
                <img src={storeLogo} alt="" className="display-store-logo" />
              ) : (
                <div className="display-logo-placeholder">
                  <span className="display-logo-placeholder-mark">勒里聚刻</span>
                </div>
              )}
            </div>
          </div>
          <div className="display-header-meta">
            <div className="display-room-line">
              <span className="display-room-line-item">
                {new Date().toLocaleDateString('zh-CN', {
                  month: 'long',
                  day: 'numeric',
                  weekday: 'short',
                })}
              </span>
              {roomInfo ? (
                <>
                  <span className="display-room-line-sep">·</span>
                  <span className="display-room-line-item display-room-line-name">{roomInfo.name}</span>
                  {roomInfo.store_name ? (
                    <>
                      <span className="display-room-line-sep">·</span>
                      <span className="display-room-line-item">{roomInfo.store_name}</span>
                    </>
                  ) : null}
                  {roomInfo.entry_time ? (
                    <>
                      <span className="display-room-line-sep">·</span>
                      <span className="display-room-line-item">{roomInfo.entry_time}</span>
                    </>
                  ) : null}
                </>
              ) : null}
            </div>
          </div>
        </header>
      </div>

      <div className="display-body">
        <CastPanel roomId={roomId} onFullscreenChange={setCastFullscreen} />

        <aside className="side-panel">
          <ServiceCallPanel roomId={roomId} showPageLink />
          <MusicPanel />
        </aside>
      </div>
    </div>
  );
}
